/**
 * ChequeoPage - Página principal del módulo Lista de Chequeo
 * Features:
 * - Lista de evaluaciones con paginación
 * - Crear nueva evaluación
 * - Ver detalle y análisis
 * - Filtros por estado y fecha
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FileCheck, BarChart3, Calendar, User, AlertTriangle } from 'lucide-react';
import { chequeoAPI } from '../../api';
import useAuthStore from '../../store/authStore';
import {
  Button,
  Card,
  Table,
  Pagination,
  SearchBar,
  Loading,
  Alert,
  Modal,
} from '../../components/common';
import ChequeoForm from '../../components/chequeo/ChequeoForm';
import ChequeoDetail from '../../components/chequeo/ChequeoDetail';
import AnalisisRiesgos from '../../components/chequeo/AnalisisRiesgos';

const ChequeoPage = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();

  // Estado
  const [evaluaciones, setEvaluaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    completada: '',
    fecha_desde: '',
    fecha_hasta: '',
  });

  // Modales
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAnalisisModal, setShowAnalisisModal] = useState(false);
  const [selectedEvaluacion, setSelectedEvaluacion] = useState(null);

  // Cargar evaluaciones
  const loadEvaluaciones = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage,
      };

      // Aplicar filtros
      if (filters.completada !== '') {
        params.completada = filters.completada === 'true';
      }
      if (filters.fecha_desde) {
        params.fecha_desde = filters.fecha_desde;
      }
      if (filters.fecha_hasta) {
        params.fecha_hasta = filters.fecha_hasta;
      }

      const response = await chequeoAPI.getEvaluaciones(params);
      
      setEvaluaciones(response.data.items || response.data);
      setTotalItems(response.data.total || response.data.length);
      setTotalPages(Math.ceil((response.data.total || response.data.length) / itemsPerPage));
    } catch (err) {
      console.error('Error al cargar evaluaciones:', err);
      setError(err.message || 'Error al cargar evaluaciones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvaluaciones();
  }, [currentPage, filters]);

  // Handlers
  const handleCreateEvaluacion = async (data) => {
    try {
      setError(null);
      await chequeoAPI.createEvaluacion(data);
      setSuccess('Evaluación iniciada correctamente');
      setShowCreateModal(false);
      loadEvaluaciones();
      
      // Limpiar mensaje después de 3 segundos
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error al crear evaluación:', err);
      setError(err.message || 'Error al crear evaluación');
    }
  };

  const handleViewDetail = (evaluacion) => {
    setSelectedEvaluacion(evaluacion);
    setShowDetailModal(true);
  };

  const handleViewAnalisis = async (evaluacion) => {
    try {
      setSelectedEvaluacion(evaluacion);
      setShowAnalisisModal(true);
    } catch (err) {
      console.error('Error al cargar análisis:', err);
      setError(err.message || 'Error al cargar análisis');
    }
  };

  const handleContinueEvaluation = (evaluacion) => {
    // Navegar al formulario de evaluación
    navigate(`/chequeo/${evaluacion.id_evaluacion}/responder`);
  };

  const handleFilterChange = (newFilters) => {
    setFilters({ ...filters, ...newFilters });
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      completada: '',
      fecha_desde: '',
      fecha_hasta: '',
    });
    setCurrentPage(1);
  };

  // Calcular nivel de riesgo basado en respuestas
  const getNivelRiesgo = (evaluacion) => {
    if (!evaluacion.completada) return null;
    
    // Esto se calculará desde el backend, pero para la UI:
    const respuestasNo = evaluacion.respuestas_no || 0;
    
    if (respuestasNo > 10) return { nivel: 'Alto', color: 'red', icon: '🔴' };
    if (respuestasNo >= 6) return { nivel: 'Medio', color: 'yellow', icon: '🟡' };
    return { nivel: 'Bajo', color: 'green', icon: '🟢' };
  };

  // Columnas de la tabla
  const columns = [
    {
      key: 'fecha_evaluacion',
      label: 'Fecha',
      render: (item) => (
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-gray-400" />
          <span>{new Date(item.fecha_evaluacion).toLocaleDateString('es-ES')}</span>
        </div>
      ),
    },
    {
      key: 'evaluador',
      label: 'Evaluador',
      render: (item) => (
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-gray-400" />
          <span>{item.usuario_evaluador?.nombre_completo || 'N/A'}</span>
        </div>
      ),
    },
    {
      key: 'completada',
      label: 'Estado',
      render: (item) => (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            item.completada
              ? 'bg-green-100 text-green-800'
              : 'bg-yellow-100 text-yellow-800'
          }`}
        >
          {item.completada ? '✓ Completada' : '⏳ En Progreso'}
        </span>
      ),
    },
    {
      key: 'riesgo',
      label: 'Nivel de Riesgo',
      render: (item) => {
        const riesgo = getNivelRiesgo(item);
        if (!riesgo) {
          return <span className="text-gray-400">-</span>;
        }
        return (
          <div className="flex items-center gap-2">
            <span>{riesgo.icon}</span>
            <span
              className={`font-medium ${
                riesgo.color === 'red'
                  ? 'text-red-600'
                  : riesgo.color === 'yellow'
                  ? 'text-yellow-600'
                  : 'text-green-600'
              }`}
            >
              {riesgo.nivel}
            </span>
          </div>
        );
      },
    },
    {
      key: 'acciones',
      label: 'Acciones',
      render: (item) => (
        <div className="flex gap-2">
          {!item.completada ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleContinueEvaluation(item)}
            >
              Continuar
            </Button>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleViewDetail(item)}
              >
                <FileCheck className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleViewAnalisis(item)}
              >
                <BarChart3 className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Lista de Chequeo</h1>
          <p className="mt-1 text-sm text-gray-500">
            Evaluación de riesgos éticos digitales
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => setShowCreateModal(true)}
          icon={Plus}
        >
          Nueva Evaluación
        </Button>
      </div>

      {/* Alertas */}
      {error && (
        <Alert
          type="error"
          title="Error"
          message={error}
          onClose={() => setError(null)}
        />
      )}

      {success && (
        <Alert
          type="success"
          title="Éxito"
          message={success}
          onClose={() => setSuccess(null)}
        />
      )}

      {/* Estadísticas rápidas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <FileCheck className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Evaluaciones</p>
              <p className="text-2xl font-bold">{totalItems}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 rounded-lg">
              <BarChart3 className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Completadas</p>
              <p className="text-2xl font-bold">
                {evaluaciones.filter((e) => e.completada).length}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <AlertTriangle className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">En Progreso</p>
              <p className="text-2xl font-bold">
                {evaluaciones.filter((e) => !e.completada).length}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filtros */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Estado
            </label>
            <select
              value={filters.completada}
              onChange={(e) => handleFilterChange({ completada: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Todos</option>
              <option value="true">Completadas</option>
              <option value="false">En Progreso</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Desde
            </label>
            <input
              type="date"
              value={filters.fecha_desde}
              onChange={(e) => handleFilterChange({ fecha_desde: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Hasta
            </label>
            <input
              type="date"
              value={filters.fecha_hasta}
              onChange={(e) => handleFilterChange({ fecha_hasta: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div className="flex items-end">
            <Button variant="outline" onClick={clearFilters} className="w-full">
              Limpiar Filtros
            </Button>
          </div>
        </div>
      </Card>

      {/* Tabla de evaluaciones */}
      <Card>
        {loading ? (
          <div className="p-8">
            <Loading size="lg" text="Cargando evaluaciones..." />
          </div>
        ) : evaluaciones.length === 0 ? (
          <div className="text-center py-12">
            <FileCheck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No hay evaluaciones registradas</p>
            <Button
              variant="primary"
              onClick={() => setShowCreateModal(true)}
              className="mt-4"
            >
              Crear Primera Evaluación
            </Button>
          </div>
        ) : (
          <>
            <Table columns={columns} data={evaluaciones} />
            {totalPages > 1 && (
              <div className="border-t p-4">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </>
        )}
      </Card>

      {/* Modal: Crear Evaluación */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Nueva Evaluación de Lista de Chequeo"
        size="lg"
      >
        <ChequeoForm
          onSubmit={handleCreateEvaluacion}
          onCancel={() => setShowCreateModal(false)}
        />
      </Modal>

      {/* Modal: Detalle de Evaluación */}
      {selectedEvaluacion && (
        <Modal
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedEvaluacion(null);
          }}
          title="Detalle de Evaluación"
          size="xl"
        >
          <ChequeoDetail evaluacion={selectedEvaluacion} />
        </Modal>
      )}

      {/* Modal: Análisis de Riesgos */}
      {selectedEvaluacion && (
        <Modal
          isOpen={showAnalisisModal}
          onClose={() => {
            setShowAnalisisModal(false);
            setSelectedEvaluacion(null);
          }}
          title="Análisis de Riesgos"
          size="xl"
        >
          <AnalisisRiesgos evaluacionId={selectedEvaluacion.id_evaluacion} />
        </Modal>
      )}
    </div>
  );
};

export default ChequeoPage;
